/**
 * Enhanced Session Security Service
 * Implements automatic timeout, IP validation, and device tracking
 */

import { supabase } from './supabase'
import { generateDeviceFingerprint, getDeviceInfo, getClientIP } from '../utils/deviceFingerprint'
import { detectBreachEvent } from './breachDetection'
import { captureException } from './errorTracking'

// Session timeout configuration (in milliseconds)
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes of inactivity
const SESSION_ABSOLUTE_TIMEOUT = 24 * 60 * 60 * 1000 // 24 hours absolute
const ACTIVITY_CHECK_INTERVAL = 60 * 1000 // Check every minute

let activityCheckInterval = null
let lastActivityTime = Date.now()

/**
 * Initialize session security
 */
export const initializeSessionSecurity = async (organizationId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const session = await supabase.auth.getSession()
    if (!session.data.session) return

    // Get device info
    const deviceInfo = getDeviceInfo()
    // IP address is optional - don't fail if we can't get it (often blocked by ad blockers)
    const ipAddress = await getClientIP().catch(() => null)

    // Create or update session record (continue even if this fails)
    try {
      await createOrUpdateSession(
        user.id,
        organizationId,
        session.data.session.access_token,
        ipAddress,
        deviceInfo
      )
    } catch (sessionError) {
      // Log but don't fail - session creation is optional for security
      console.warn('Could not create session record:', sessionError)
      // Continue with other security measures
    }

    // NOTE: Session security (timeouts, IP/device checks, redirects) has been disabled
    // to avoid unwanted reloads/logouts when switching tabs or on visibility changes.
    // We still allow session records to be created above, but we do NOT start
    // any timers or add any event listeners here.

    return { success: true }
  } catch (error) {
    // Don't throw - session security should be non-blocking
    console.warn('Error initializing session security:', error)
    captureException(error, { context: 'session_security_init' })
    return { success: false, error: error.message }
  }
}

/**
 * Create or update session record
 */
const createOrUpdateSession = async (userId, organizationId, sessionToken, ipAddress, deviceInfo) => {
  try {
    // Hash the session token for storage (don't store raw token)
    const tokenHash = hashToken(sessionToken)

    // Check if session exists (don't use .single() to avoid errors if none exists)
    const { data: existingSessions } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('session_token_hash', tokenHash)
      .eq('is_active', true)
      .limit(1)

    const existingSession = existingSessions?.[0]

    const expiresAt = new Date(Date.now() + SESSION_ABSOLUTE_TIMEOUT)
    const sessionData = {
      user_id: userId,
      organization_id: organizationId || null, // Allow null organization_id
      session_token: sessionToken, // Store actual token (required by schema)
      session_token_hash: tokenHash, // Also store hash for security
      ip_address: ipAddress || null, // Allow null IP
      user_agent: navigator.userAgent,
      device_info: deviceInfo || {},
      device_fingerprint: deviceInfo?.fingerprint || null,
      is_active: true,
      last_activity_at: new Date().toISOString(),
      expires_at: expiresAt.toISOString(),
    }

    if (existingSession) {
      // Update existing session
      const { error } = await supabase
        .from('user_sessions')
        .update(sessionData)
        .eq('id', existingSession.id)

      if (error) {
        console.warn('Error updating session:', error)
        throw error
      }
      return existingSession.id
    } else {
      // Create new session
      const { data, error } = await supabase
        .from('user_sessions')
        .insert(sessionData)
        .select()
        .single()

      if (error) {
        console.warn('Error creating session:', error)
        throw error
      }
      return data?.id
    }
  } catch (error) {
    // Don't log as error - session creation is optional
    console.warn('Could not create/update session record:', error.message || error)
    throw error
  }
}

/**
 * Hash session token (simple hash for storage)
 */
const hashToken = (token) => {
  // Simple hash - in production, use a proper hashing library
  let hash = 0
  for (let i = 0; i < token.length; i++) {
    const char = token.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return Math.abs(hash).toString(36)
}

/**
 * Start activity monitoring
 */
const startActivityMonitoring = (organizationId) => {
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval)
  }

  activityCheckInterval = setInterval(async () => {
    await checkSessionValidity(organizationId)
  }, ACTIVITY_CHECK_INTERVAL)
}

/**
 * Check session validity
 */
const checkSessionValidity = async (_organizationId) => {
  // Session validity checks (timeouts, IP/device enforcement) are disabled.
  // We keep this function as a no-op to avoid breaking callers.
  return
}

/**
 * Verify IP address matches session
 */
const verifyIPAddress = async (organizationId, userId) => {
  try {
    const currentIP = await getClientIP()
    if (!currentIP) return true // If we can't get IP, allow (fallback)

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return false

    const tokenHash = hashToken(session.access_token)

    const { data: sessionRecord } = await supabase
      .from('user_sessions')
      .select('ip_address')
      .eq('user_id', userId)
      .eq('session_token_hash', tokenHash)
      .eq('is_active', true)
      .single()

    if (!sessionRecord) return false

    // Allow if IP matches or if IP validation is not strict
    // In production, you might want stricter IP validation
    return sessionRecord.ip_address === currentIP || !sessionRecord.ip_address
  } catch (error) {
    console.error('Error verifying IP address:', error)
    return true // Fail open for now
  }
}

/**
 * Verify device fingerprint matches session
 */
const verifyDeviceFingerprint = async (userId) => {
  try {
    const currentFingerprint = generateDeviceFingerprint()
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return false

    const tokenHash = hashToken(session.access_token)

    const { data: sessionRecord } = await supabase
      .from('user_sessions')
      .select('device_fingerprint')
      .eq('user_id', userId)
      .eq('session_token_hash', tokenHash)
      .eq('is_active', true)
      .single()

    if (!sessionRecord) return false

    return sessionRecord.device_fingerprint === currentFingerprint
  } catch (error) {
    console.error('Error verifying device fingerprint:', error)
    return true // Fail open for now
  }
}

/**
 * Update last activity timestamp
 */
const updateLastActivity = async (userId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    const tokenHash = hashToken(session.access_token)

    await supabase
      .from('user_sessions')
      .update({ last_activity_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('session_token_hash', tokenHash)
      .eq('is_active', true)

    lastActivityTime = Date.now()
  } catch (error) {
    console.error('Error updating last activity:', error)
  }
}

/**
 * Handle session timeout
 */
const handleSessionTimeout = async (_organizationId, reason) => {
  // Timeouts are currently disabled; log to console only.
  console.warn(`Session timeout (${reason}) detected but redirects are disabled.`)
  return
}

/**
 * Handle IP mismatch
 * NOTE: This function is currently disabled and should not be called.
 * It's kept for reference but uses window.location which causes hard reloads.
 * If needed in the future, use React Router navigation instead.
 */
const handleIPMismatch = async (organizationId, userId) => {
  // DISABLED: This function causes hard page reloads which lose user data.
  // If IP mismatch detection is needed, it should use React Router navigation
  // and preserve form state via localStorage.
  console.warn('IP mismatch detected but handling is disabled to prevent data loss')
  
  // Log the event but don't take action that would cause reload
  try {
    await detectBreachEvent(
      organizationId,
      'unauthorized_access',
      'high',
      'IP address mismatch detected - potential session hijacking (logged only)',
      [],
      [userId],
      await getClientIP()
    )
  } catch (error) {
    console.error('Error logging IP mismatch:', error)
    captureException(error, { context: 'ip_mismatch' })
  }
  
  // DO NOT call signOut() or window.location.href here - it causes data loss
}

/**
 * Handle device fingerprint mismatch
 * NOTE: This function is currently disabled and should not be called.
 * It's kept for reference but uses window.location which causes hard reloads.
 * If needed in the future, use React Router navigation instead.
 */
const handleDeviceMismatch = async (organizationId, userId) => {
  // DISABLED: This function causes hard page reloads which lose user data.
  // If device mismatch detection is needed, it should use React Router navigation
  // and preserve form state via localStorage.
  console.warn('Device mismatch detected but handling is disabled to prevent data loss')
  
  // Log the event but don't take action that would cause reload
  try {
    await detectBreachEvent(
      organizationId,
      'unauthorized_access',
      'high',
      'Device fingerprint mismatch detected - potential session hijacking (logged only)',
      [],
      [userId],
      await getClientIP()
    )
  } catch (error) {
    console.error('Error logging device mismatch:', error)
    captureException(error, { context: 'device_mismatch' })
  }
  
  // DO NOT call signOut() or window.location.href here - it causes data loss
}

/**
 * Handle session expired
 */
const handleSessionExpired = () => {
  // Session expired handling is disabled to prevent hard redirects.
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval)
  }
}

/**
 * Setup activity tracking
 */
const setupActivityTracking = () => {
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
  
  const updateActivity = () => {
    lastActivityTime = Date.now()
  }

  events.forEach((event) => {
    document.addEventListener(event, updateActivity, { passive: true })
  })
}

/**
 * Handle visibility change (tab/window focus)
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    // Tab became visible, check session
    lastActivityTime = Date.now()
  }
}

/**
 * Cleanup session security
 */
export const cleanupSessionSecurity = () => {
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval)
    activityCheckInterval = null
  }
  
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

/**
 * Manually refresh session
 */
export const refreshSession = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return false

    // Refresh the session
    const { data, error } = await supabase.auth.refreshSession(session)
    
    if (error) throw error
    
    // Update session record
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const deviceInfo = getDeviceInfo()
      const ipAddress = await getClientIP()
      const tokenHash = hashToken(data.session.access_token)
      
      await supabase
        .from('user_sessions')
        .update({
          last_activity_at: new Date().toISOString(),
          expires_at: new Date(data.session.expires_at).toISOString(),
        })
        .eq('user_id', user.id)
        .eq('session_token_hash', tokenHash)
    }

    lastActivityTime = Date.now()
    return true
  } catch (error) {
    console.error('Error refreshing session:', error)
    captureException(error, { context: 'session_refresh' })
    return false
  }
}

